import React, { useMemo } from 'react';
import styles from './HistoricalListingsContainer.module.css';
import HistoricalListingCard from './HistoricalListingCard';
import { getProcessedHistoricalListings } from '../../services/historicalDataService';

const HistoricalListingsContainer = ({ listings, isSplitMode = false, onListingHover, onListingClick, highlightedListingId }) => {
  // Use memoization to efficiently process data only when listings change
  const { grouped, sortedYears } = useMemo(() => {
    // Try to get pre-processed data from the singleton service first
    const processedData = window._processedHistoricalData;
    
    if (processedData) {
      console.log('[HistoricalListingsContainer] Using pre-processed data');
      return {
        grouped: processedData.grouped,
        sortedYears: processedData.sortedYears
      };
    }
    
    console.log('[HistoricalListingsContainer] Processing historical data');
    
    // If we need to process on the fly, use the same logic as the service
    const grouped = {};
    
    listings.forEach(listing => {
      if (!listing['Close Date']) return; // Skip entries with no close date
      
      const date = new Date(listing['Close Date']);
      if (isNaN(date)) return; // Skip entries with invalid dates
      
      let year = date.getFullYear();
      if (isNaN(year) || year < 1900 || year > 2100) return; // Skip entries with invalid years
      
      // Merge 2025 listings into 2024
      if (year === 2025) year = 2024;
      
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(listing);
    });

    // Sort years in descending order (newest first)
    const sortedYears = Object.keys(grouped).sort((a, b) => b - a);
    
    // Sort listings within each year by date (newest first)
    sortedYears.forEach(year => {
      // For 2024, make sure 2025 listings come first, then sort by date
      if (year === '2024') {
        grouped[year].sort((a, b) => {
          const dateA = new Date(a['Close Date']);
          const dateB = new Date(b['Close Date']);
          const yearA = dateA.getFullYear();
          const yearB = dateB.getFullYear();
          
          // If one is from 2025 and the other isn't, prioritize 2025
          if (yearA === 2025 && yearB !== 2025) return -1;
          if (yearA !== 2025 && yearB === 2025) return 1;
          
          // Otherwise sort by date (newest first)
          return dateB - dateA;
        });
      } else {
        // Normal sorting for other years
        grouped[year].sort((a, b) => {
          return new Date(b['Close Date']) - new Date(a['Close Date']);
        });
      }
    });
    
    // Cache the processed results for future use
    const result = { grouped, sortedYears };
    window._processedHistoricalData = result;
    
    // Load fully processed data asynchronously for future visits
    getProcessedHistoricalListings().then(data => {
      window._processedHistoricalData = data;
      console.log('[HistoricalListingsContainer] Updated pre-processed data cache');
    });
    
    return result;
  }, [listings]);

  return (
    <div id="historical-listings-list" className={`${styles.container} ${isSplitMode ? styles.splitMode : styles.galleryMode}`}>
      {sortedYears
        .filter(year => year !== 'NaN' && year !== 'undefined' && year !== 'null')
        .map(year => (
          <div key={year} className={styles.yearGroup}>
            <div className={styles.yearDivider}>{year === '2024' ? 'LAST YEAR' : year}</div>
            <div className={styles.listingsGroup}>
              {grouped[year].map((listing, index) => (
                <HistoricalListingCard 
                  key={index} 
                  listing={listing} 
                  isSplitMode={isSplitMode}
                  onHover={onListingHover} 
                  onClick={onListingClick}
                  highlighted={highlightedListingId === (listing['List Number'] || listing.id || listing['MLS #'] || listing['ML #'] || listing['MLS Number'] || listing['MLSNumber'] || (listing['Street Address'] && listing['City'] ? `${listing['Street Address']}-${listing['City']}` : undefined))}
                />
              ))}
            </div>
          </div>
        ))}
    </div>
  );
};

export default HistoricalListingsContainer;
